<script setup>
import { ref, computed, watch } from 'vue';
import VueDatePicker from '@vuepic/vue-datepicker';
import Panel from '@molecules/components/Panel.vue';
import { trans } from '@elements/translation';
import { addDays, differenceInDays, endOfMonth, getHumanDate } from '@elements/tools/date';
import { displayCurrencyWithoutCurrencyCode } from '@elements/number-formatter';
import useSubscriptionStore from '../../store/subscriptions';

const emit = defineEmits([ 'next-step' ]);

const subscriptionStore = useSubscriptionStore();

const minDisabledDate = computed(() => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1);
});

const localStartDate = ref(subscriptionStore.invoice.startDate ?? null);

const localEndDate = ref(subscriptionStore.invoice.endDate ?? null);
const isUnlimited = ref((subscriptionStore.invoice.startDate && !subscriptionStore.invoice.endDate) ?? false);
const startDateError = ref(null);
const displayFormat = ref('dd/MM/yyyy');

const endDateError = ref(null);
const minEndDisabledDate = computed(() => (localStartDate.value ? addDays(new Date(localStartDate.value), 1) : addDays(minDisabledDate.value, 1)));

const onUnlimitedChange = () => {
  if (isUnlimited.value) {
    localEndDate.value = null;
    endDateError.value = null;
  }
};

const validateStartDate = () => {
  if (!localStartDate.value) {
    startDateError.value = trans('gestion.subscription.step.3.general.error.required');
    return;
  }

  if (new Date(localStartDate.value) < minDisabledDate.value) {
    startDateError.value = trans('gestion.subscription.step.3.subscription.error.invalid_date');
    return;
  }

  startDateError.value = null;
};

const validateDateRange = () => {
  if (isUnlimited.value || !localEndDate.value) {
    endDateError.value = null;
    return;
  }
  const start = new Date(localStartDate.value);
  const end = new Date(localEndDate.value);
  endDateError.value = end > start
    ? null
    : trans('gestion.subscription.step.5.error.end_after_start');
};

const onEndDateChange = (value) => {
  if (value) {
    isUnlimited.value = false;
  }
  validateDateRange();
};

watch(localStartDate, () => { validateStartDate(); validateDateRange(); });

const calculateProratedAmount = (start) => {
  const endOfCurrentMonth = endOfMonth(start);
  const remainingDays = differenceInDays(endOfCurrentMonth, start) + 1;
  const totalDays = differenceInDays(endOfCurrentMonth, new Date(start.getFullYear(), start.getMonth(), 1)) + 1;
  const proratedAmount = (remainingDays / totalDays) * (subscriptionStore.invoice.amount / 100);

  return displayCurrencyWithoutCurrencyCode(proratedAmount);
};

const dynamicPanelContent = computed(() => {
  if (!localStartDate.value) {
    return null;
  }

  const start = new Date(localStartDate.value);
  const isStartFirstOfMonth = start.getDate() === 1;

  if (isStartFirstOfMonth) {
    return null;
  }

  const formattedDate = getHumanDate(start);
  const proratedAmount = calculateProratedAmount(start);
  return trans('gestion.subscription.contract.explanation', {
    formattedDate,
    proratedAmount,
    amount: displayCurrencyWithoutCurrencyCode(subscriptionStore.invoice.amount / 100),
  });
});

const isNextDisabled = computed(() => !localStartDate.value
  || startDateError.value
  || (!localEndDate.value && !isUnlimited.value)
  || endDateError.value);

const onClickNext = () => {
  validateStartDate();
  validateDateRange();
  if (!startDateError.value && !endDateError.value) {
    subscriptionStore.setSubscription({
      startDate: localStartDate.value,
      endDate: isUnlimited.value ? null : localEndDate.value,
    });
    emit('next-step');
  }
};

</script>

<template>
  <div class="stepper-subscription infos-subscription">
    <h3 class="stepper-subscription__title">
      {{ trans('gestion.subscription.step.5.title') }}
    </h3>
    <div class="infos-subscription__form">
      <div class="infos-subscription__form-group">
        <label class="infos-subscription__label">{{ trans('gestion.subscription.step.5.start_date') }}</label>
        <VueDatePicker
          v-model="localStartDate"
          name="start_date"
          locale="fr"
          :format="displayFormat"
          :enable-time-picker="false"
          auto-apply
          :class="{ error: startDateError }"
          :start-date="minDisabledDate"
          :min-date="minDisabledDate"
          @blur="validateStartDate"
        >
          <template #input-icon>
            <span class="datepicker__icon datepicker__icon--datepicker" />
          </template>
        </VueDatePicker>
        <span v-if="startDateError" class="error-message">{{ startDateError }}</span>
      </div>
      <panel
        v-if="dynamicPanelContent"
        type="info"
        :content="dynamicPanelContent"
        icon="ph_lightbulb-fill_blue"
      />
      <div class="infos-subscription__form-group">
        <label class="infos-subscription__label">{{ trans('gestion.subscription.step.5.end_date') }}</label>
        <VueDatePicker
          v-model="localEndDate"
          name="end_date"
          locale="fr"
          :format="displayFormat"
          :enable-time-picker="false"
          :min-date="minEndDisabledDate"
          :start-date="minEndDisabledDate"
          :class="{ error: endDateError }"
          auto-apply
          @update:modelValue="onEndDateChange"
        >
          <template #input-icon>
            <span class="datepicker__icon datepicker__icon--datepicker" />
          </template>
        </VueDatePicker>
        <span v-if="endDateError" class="error-message">{{ endDateError }}</span>
        <div class="infos-subscription__form-group infos-subscription__form-group--checkbox">
          <input
            id="unlimited-checkbox"
            v-model="isUnlimited"
            type="checkbox"
            class="checkbox"
            @change="onUnlimitedChange"
          >
          <label for="unlimited-checkbox">{{ trans('global.unlimited') }}</label>
        </div>
      </div>
    </div>
    <p class="stepper-subscription__mandatory-infos">
      {{ trans('gestion.subscription.general.mandatory_fields') }}
    </p>
    <button class="btn btn--primary stepper-subscription__button" :disabled="isNextDisabled" @click="onClickNext">
      {{ trans('gestion.subscription.step.5.action') }}
    </button>
  </div>
</template>
